import React, { useEffect, useMemo, useState } from "react";

import "./CountryFlag.css";

const EMPTY_MARKERS = new Set(["", "—", "-", "–", "n/a", "na", "none"]);

/**
 * Extract ISO 3166-1 alpha-2 for flagcdn URLs; returns null if no reliable code.
 */
export function normalizeIsoCountryCode(input) {
  if (input == null) return null;
  const raw = String(input).trim();
  if (!raw || EMPTY_MARKERS.has(raw.toLowerCase())) return null;

  const strippedLeadTrail = raw.replace(/^[^A-Za-z]+/, "").replace(/[^A-Za-z]+$/, "");
  const core = strippedLeadTrail.length >= 2 ? strippedLeadTrail : raw;
  if (/^[a-z]{2}$/i.test(core)) return core.slice(0, 2).toLowerCase();

  const tokens = raw.split(/[\s,/|]+/).filter(Boolean);
  for (const t of tokens) {
    if (/^[a-z]{2}$/i.test(t)) return t.slice(0, 2).toLowerCase();
  }
  return null;
}

function isEmpty(countryCode) {
  if (countryCode == null) return true;
  const s = String(countryCode).trim();
  return !s || EMPTY_MARKERS.has(s.toLowerCase());
}

/**
 * Renders flagcdn.com PNG + country code text. UI-only; no persisted URLs.
 *
 * @param {string} [countryCode] — ISO alpha-2 or raw text (e.g. display name)
 */
export default function CountryFlag({ countryCode, className }) {
  const raw = countryCode != null ? String(countryCode).trim() : "";
  const [hideImg, setHideImg] = useState(false);

  const iso = useMemo(() => normalizeIsoCountryCode(raw), [raw]);

  useEffect(() => {
    setHideImg(false);
  }, [iso, raw]);

  const flagUrl = iso ? `https://flagcdn.com/24x18/${iso}.png` : null;

  if (isEmpty(countryCode)) {
    return (
      <span className={`country-flag country-flag--empty ${className || ""}`.trim()}>
        -
      </span>
    );
  }

  const codeLabel = iso ? iso.toUpperCase() : raw;

  return (
    <span className={`country-flag ${className || ""}`.trim()} title={raw}>
      {flagUrl && !hideImg ? (
        <img
          key={flagUrl}
          src={flagUrl}
          alt=""
          width={24}
          height={18}
          className="country-flag__img"
          loading="lazy"
          decoding="async"
          onError={() => setHideImg(true)}
        />
      ) : null}
      <span className="country-flag__code">{codeLabel}</span>
    </span>
  );
}
